/**
 * Раздел «Настройки» менеджера (правки Артёма).
 *
 * ДВЕ ВКЛАДКИ: «Номера» (список номеров отеля) и «Типы номеров». Список
 * номеров и типы перенесены сюда из прежнего экрана «Номера» (RoomsBoard).
 * Шахматка/«Календарь номеров» остаётся отдельным пунктом навигации и сюда
 * НЕ входит.
 *
 * Вкладка «Номера»: список с фильтрами этаж/статус; клик по номеру открывает
 * ОТДЕЛЬНУЮ форму редактирования (RoomEditForm) с кнопками снизу
 * «Сохранить»/«Назад» — там же этаж/№/тип + фото номера.
 *
 * RBAC: проп canManage прокидывается в обе вкладки (создание/правка номеров
 * и типов — только управляющим ролям).
 */
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import { ROOM_STATUSES, type RoomStatus } from '@hsc/types';
import { resolveTenantSlug } from '../../lib/tenant-slug.js';
import { ROOM_STATUS_LABEL, ROOM_STATUS_TONE } from '../../lib/fleet-format.js';
import { useFloors, useRoomTypes, useRooms } from './useRooms.js';
import { RoomCreateForm } from './RoomCreateForm.js';
import { RoomEditForm } from './RoomEditForm.js';
import { RoomTypesPanel } from './RoomTypesPanel.js';
import { AmenitiesPanel } from './AmenitiesPanel.js';
import { RequestTypesPanel } from './RequestTypesPanel.js';
import { ShiftTemplatesPanel } from './ShiftTemplatesPanel.js';
import { DepartmentsPanel } from '../staff/DepartmentsPanel.js';
import { RolesPanel } from '../staff/RolesPanel.js';
import { AutoTasksPanel } from './AutoTasksPanel.js';

type SettingsTab =
  | 'rooms'
  | 'types'
  | 'amenities'
  | 'request-types'
  | 'shift-templates'
  | 'auto-tasks'
  | 'departments'
  | 'roles';

export interface SettingsPageProps {
  canManage?: boolean;
  initialTab?: SettingsTab;
}

export function SettingsPage({
  canManage = false,
  initialTab = 'rooms',
}: SettingsPageProps): JSX.Element {
  const { pathname } = useLocation();
  const urlTab = ((): SettingsTab | undefined => {
    const seg = pathname.split('/').filter(Boolean).pop();
    return seg === 'departments' ||
      seg === 'roles' ||
      seg === 'types' ||
      seg === 'rooms' ||
      seg === 'amenities' ||
      seg === 'request-types' ||
      seg === 'shift-templates' ||
      seg === 'auto-tasks'
      ? (seg as SettingsTab)
      : undefined;
  })();
  const [tab, setTab] = useState<SettingsTab>(urlTab ?? initialTab);
  // Когда задан — показываем форму редактирования вместо списка.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Когда true — показываем ОТДЕЛЬНЫЙ экран создания номера вместо списка.
  const [creating, setCreating] = useState(false);

  return (
    <section data-testid="settings-page">
      <h1 className="hsc-page__title">Настройки</h1>

      <div className="fleet-tabs" data-testid="settings-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'rooms'}
          className={`fleet-tab${tab === 'rooms' ? ' is-active' : ''}`}
          onClick={() => {
            setTab('rooms');
            setEditingId(null);
            setCreating(false);
          }}
          data-testid="settings-tab-rooms"
        >
          Номера
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'types'}
          className={`fleet-tab${tab === 'types' ? ' is-active' : ''}`}
          onClick={() => setTab('types')}
          data-testid="settings-tab-types"
        >
          Типы номеров
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'amenities'}
          className={`fleet-tab${tab === 'amenities' ? ' is-active' : ''}`}
          onClick={() => {
            setTab('amenities');
            setEditingId(null);
          }}
          data-testid="settings-tab-amenities"
        >
          Удобства
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'request-types'}
          className={`fleet-tab${tab === 'request-types' ? ' is-active' : ''}`}
          onClick={() => {
            setTab('request-types');
            setEditingId(null);
          }}
          data-testid="settings-tab-request-types"
        >
          Типы заявок
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'shift-templates'}
          className={`fleet-tab${tab === 'shift-templates' ? ' is-active' : ''}`}
          onClick={() => {
            setTab('shift-templates');
            setEditingId(null);
          }}
          data-testid="settings-tab-shift-templates"
        >
          Шаблоны смен
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'auto-tasks'}
          className={`fleet-tab${tab === 'auto-tasks' ? ' is-active' : ''}`}
          onClick={() => {
            setTab('auto-tasks');
            setEditingId(null);
            setCreating(false);
          }}
          data-testid="settings-tab-auto-tasks"
        >
          Уборка/Автозаявки
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'departments'}
          className={`fleet-tab${tab === 'departments' ? ' is-active' : ''}`}
          onClick={() => setTab('departments')}
          data-testid="settings-tab-departments"
        >
          Отделы
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'roles'}
          className={`fleet-tab${tab === 'roles' ? ' is-active' : ''}`}
          onClick={() => setTab('roles')}
          data-testid="settings-tab-roles"
        >
          Роли
        </button>
      </div>

      {tab === 'rooms' ? (
        creating ? (
          <RoomCreateForm onBack={() => setCreating(false)} />
        ) : editingId !== null ? (
          <RoomEditForm roomId={editingId} onBack={() => setEditingId(null)} />
        ) : (
          <RoomsSettingsList
            canManage={canManage}
            onOpen={setEditingId}
            onCreate={() => setCreating(true)}
          />
        )
      ) : tab === 'types' ? (
        <RoomTypesPanel canManage={canManage} />
      ) : tab === 'amenities' ? (
        <AmenitiesPanel canManage={canManage} />
      ) : tab === 'request-types' ? (
        <RequestTypesPanel canManage={canManage} />
      ) : tab === 'shift-templates' ? (
        <ShiftTemplatesPanel canManage={canManage} />
      ) : tab === 'auto-tasks' ? (
        <AutoTasksPanel canManage={canManage} />
      ) : tab === 'departments' ? (
        <DepartmentsPanel canManage={canManage} />
      ) : (
        <RolesPanel canManage={canManage} />
      )}
    </section>
  );
}

function RoomsSettingsList({
  canManage,
  onOpen,
  onCreate,
}: {
  canManage: boolean;
  onOpen: (id: string) => void;
  onCreate: () => void;
}): JSX.Element {
  const slug = useMemo(() => resolveTenantSlug(), []);
  const [floor, setFloor] = useState<number | undefined>(undefined);
  const [status, setStatus] = useState<RoomStatus | ''>('');

  const params = useMemo(
    () => ({
      ...(floor !== undefined ? { floor } : {}),
      ...(status !== '' ? { status } : {}),
    }),
    [floor, status],
  );
  const { data, isLoading, isError } = useRooms(params);
  const floorsQ = useFloors();
  const typesQ = useRoomTypes(slug);

  const typeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of typesQ.data?.room_types ?? []) map.set(t.id, t.name);
    return map;
  }, [typesQ.data]);

  return (
    <section data-testid="rooms-settings-list">
      {canManage && (
        <div className="fleet-toolbar" data-testid="room-create-toolbar">
          <Button size="sm" onClick={onCreate} data-testid="room-create-open">
            + Добавить номер
          </Button>
        </div>
      )}

      <div className="fleet-floors" data-testid="rooms-floors">
        <button
          type="button"
          className={`fleet-chip${floor === undefined ? ' is-active' : ''}`}
          onClick={() => setFloor(undefined)}
          data-testid="floor-all"
        >
          Все этажи
        </button>
        {(floorsQ.data?.floors ?? []).map((f) => (
          <button
            key={f.number}
            type="button"
            className={`fleet-chip${floor === f.number ? ' is-active' : ''}`}
            onClick={() => setFloor(f.number)}
            data-testid={`floor-${f.number}`}
          >
            {f.number} этаж · {f.available_count}/{f.room_count}
          </button>
        ))}
      </div>

      <div className="fleet-statuses" data-testid="rooms-status-filter">
        <button
          type="button"
          className={`fleet-chip${status === '' ? ' is-active' : ''}`}
          onClick={() => setStatus('')}
          data-testid="status-all"
        >
          Все статусы
        </button>
        {ROOM_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={`fleet-chip fleet-chip--${ROOM_STATUS_TONE[s]}${status === s ? ' is-active' : ''}`}
            onClick={() => setStatus(s)}
            data-testid={`status-${s}`}
          >
            {ROOM_STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="fleet-grid" data-testid="rooms-skeleton">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} height={96} radius={16} />
          ))}
        </div>
      ) : isError || !data ? (
        <Card>
          <p role="alert">Не удалось загрузить номера. Попробуйте обновить страницу.</p>
        </Card>
      ) : data.items.length === 0 ? (
        <Card>
          <p>Под фильтр ничего не подходит. Снимите фильтры или добавьте номера.</p>
        </Card>
      ) : (
        <ul className="fleet-grid" data-testid="rooms-grid">
          {data.items.map((room) => (
            <li key={room.id} className="fleet-card" data-testid={`room-${room.number}`}>
              <button
                type="button"
                className="rooms-list__open"
                onClick={() => onOpen(room.id)}
                data-testid={`room-${room.number}-open`}
              >
                <div className="fleet-card__head">
                  <span className="fleet-card__title">№ {room.number}</span>
                  <span
                    className={`fleet-badge fleet-badge--${ROOM_STATUS_TONE[room.status]}`}
                    data-testid={`room-${room.number}-status`}
                  >
                    {ROOM_STATUS_LABEL[room.status]}
                  </span>
                </div>
                <div className="fleet-card__meta">
                  <span>{room.floor} этаж</span>
                  {typeName.get(room.type_id) !== undefined && (
                    <span>· {typeName.get(room.type_id)}</span>
                  )}
                </div>
                <span className="rooms-list__hint">Открыть и редактировать →</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
